import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Plus, Search, Users, Inbox, Check, X, Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import { useToast } from "@/hooks/use-toast";
import {
  fetchCircleEnergySnapshotInDb,
  fetchCircleTaskCompletionCounts,
  joinCircleInDb,
  listDiscoverCircles,
  listMyCircles,
  listMyCircleInvitesInDb,
  respondToCircleInviteInDb,
  type CircleSummary,
} from "@/lib/supabase/circles";

function getCompatibilityPercent(circle: CircleSummary) {
  const base = 70 + Math.min(12, circle.membersCount) * 1.6;
  return Math.max(60, Math.min(98, Math.round(base)));
}

function getCircleBadge(circle: CircleSummary) {
  const firstRule = circle.rules?.all?.[0];
  if (!firstRule) return "UYUM";

  const labelMap: Record<string, string> = {
    destinyNumber: "Yaşam Yolu",
    expressionNumber: "İfade",
    heartDesireNumber: "Kalp Arzusu",
    personalityNumber: "Kişilik",
    birthDayNumber: "Doğum Günü",
    karmicDebt: "Karmik Borç",
  };

  const key = labelMap[firstRule.key] ?? "Uyum";
  return `${key} ${firstRule.value}`;
}

function parseCircleActivityMeta(activity: string | null) {
  const raw = (activity ?? "").trim();
  if (!raw) return { ritualTitle: "Meditasyon" };

  const [, right] = raw.split("|").map((x) => x.trim());
  const source = right ?? raw;
  const first = source
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)[0];

  if (!first) return { ritualTitle: "Meditasyon" };

  const map: Record<string, string> = {
    meditasyon: "Sabah Meditasyonu (15dk)",
    nefes: "Nefes Çalışması",
    rituel: "Ritüel Uygulaması",
    sohbet: "Paylaşım Ritüeli",
  };

  return { ritualTitle: map[first] ?? first };
}

function CircleCard(props: {
  circle: CircleSummary;
  energyPercent: number;
  onOpen: () => void;
  onEdit?: (() => void) | null;
}) {
  const { circle, energyPercent, onOpen, onEdit } = props;
  const badge = getCircleBadge(circle);
  const ritual = parseCircleActivityMeta(circle.activity);

  return (
    <div className="rounded-2xl border border-[var(--panel-primary)]/20 bg-white/90 p-5 shadow-sm dark:border-[var(--panel-primary)]/30 dark:bg-[#19122a]">
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-full border border-[var(--panel-primary)]/35 bg-[var(--panel-primary)]/12 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-[var(--panel-primary)] dark:border-[var(--panel-primary)]/45 dark:bg-[var(--panel-primary)]/20 dark:text-[#e9d5ff]">
          {badge}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-700 dark:text-slate-300">{circle.membersCount} Üye</span>
          {onEdit ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-slate-600 hover:text-[var(--panel-primary)]"
              onClick={onEdit}
              title="Çemberi düzenle"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>

      <h4 className="mt-4 text-[24px] font-bold leading-tight text-slate-900 dark:text-white">{circle.name}</h4>
      <p className="mt-2 min-h-[40px] text-sm text-slate-700 dark:text-slate-300">{circle.description || "Ruhsal denge ve paylaşımla büyüyen çember."}</p>

      <div className="mt-4 rounded-xl border border-[var(--panel-primary)]/15 bg-[var(--panel-primary)]/5 p-3 dark:border-[var(--panel-primary)]/25 dark:bg-[var(--panel-primary)]/10">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Günün Ritüeli</div>
        <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
          <BookOpen className="h-4 w-4 text-[var(--panel-primary)] dark:text-[#d8b4fe]" />
          {ritual.ritualTitle}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="relative h-10 w-10 rounded-full"
            style={{
              background: `conic-gradient(var(--panel-primary) ${Math.max(0, Math.min(100, energyPercent)) * 3.6}deg, rgb(226 232 240) 0deg)`,
            }}
          >
            <div className="absolute inset-[4px] rounded-full bg-white dark:bg-slate-950" />
            <div className="absolute inset-0 grid place-items-center text-[9px] font-bold text-slate-800 dark:text-slate-200">{energyPercent}%</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Enerji Skoru</div>
            <div className="text-lg font-extrabold text-slate-900 dark:text-white">{energyPercent}%</div>
          </div>
        </div>

        <Button
          className="rounded-xl border border-[var(--panel-primary)] bg-[var(--panel-primary)] px-5 font-semibold text-white hover:bg-[var(--panel-primary)]/90 dark:border-[var(--panel-primary)] dark:bg-[var(--panel-primary)] dark:text-white dark:hover:bg-[var(--panel-primary)]/85"
          onClick={onOpen}
        >
          {"Giriş Yap"}
        </Button>
      </div>
    </div>
  );
}

function RecommendedRow(props: {
  circle: CircleSummary;
  onJoin: () => void;
  onOpen: () => void;
}) {
  const { circle, onJoin, onOpen } = props;
  const percent = getCompatibilityPercent(circle);

  return (
    <div className="group flex flex-col items-center gap-6 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-colors hover:border-[var(--panel-primary)]/40 dark:border-slate-800 dark:bg-slate-950 md:flex-row">
      <div className="grid h-20 w-20 shrink-0 place-items-center rounded-2xl bg-[var(--panel-primary)]/10 text-center">
        <div>
          <div className="text-2xl font-black leading-none text-[var(--panel-primary)]">{percent}%</div>
          <div className="mt-1 text-[10px] font-bold uppercase tracking-wide text-[var(--panel-primary)]/70">Uyum</div>
        </div>
      </div>

      <div className="min-w-0 flex-1 text-center md:text-left">
        <div className="truncate text-xl font-bold text-slate-900 dark:text-white">{circle.name}</div>
        <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">{circle.membersCount} üye</div>
      </div>

      <div className="shrink-0">
        <Button
          variant="outline"
          className="rounded-xl border-2 border-[var(--panel-primary)] text-[var(--panel-primary)] hover:bg-[var(--panel-primary)] hover:text-white"
          onClick={onJoin}
        >
          İncele & Katıl
        </Button>
        <Button variant="ghost" className="ml-2 rounded-xl" onClick={onOpen}>
          Aç
        </Button>
      </div>
    </div>
  );
}

export default function UserPanelCemberler() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, loading } = useSupabaseSession();
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState("");

  const myCirclesQuery = useQuery({
    queryKey: ["panel-circles-mine", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listMyCircles(String(user?.id ?? "")),
    staleTime: 10_000,
  });

  const discoverQuery = useQuery({
    queryKey: ["panel-circles-discover", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listDiscoverCircles({ userId: String(user?.id ?? ""), limit: 8 }),
    staleTime: 10_000,
  });

  const inviteInboxQuery = useQuery({
    queryKey: ["panel-circle-invites", user?.id],
    enabled: Boolean(user?.id) && !loading,
    queryFn: () => listMyCircleInvitesInDb(),
    staleTime: 8_000,
  });

  const activeCircles = myCirclesQuery.data ?? [];
  const recommendedCircles = discoverQuery.data ?? [];
  const pendingInvites = (inviteInboxQuery.data ?? []).filter((invite) => invite.status === "pending");
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase("tr-TR");

  const filteredActiveCircles = useMemo(() => {
    if (!normalizedSearchTerm) return activeCircles;
    return activeCircles.filter((circle) => circle.name.toLocaleLowerCase("tr-TR").includes(normalizedSearchTerm));
  }, [activeCircles, normalizedSearchTerm]);

  const filteredRecommendedCircles = useMemo(() => {
    if (!normalizedSearchTerm) return recommendedCircles;
    return recommendedCircles.filter((circle) => circle.name.toLocaleLowerCase("tr-TR").includes(normalizedSearchTerm));
  }, [recommendedCircles, normalizedSearchTerm]);

  const energyScoresQuery = useQuery({
    queryKey: ["panel-circle-energy-scores", activeCircles.map((c) => c.id).join(",")],
    enabled: activeCircles.length > 0,
    queryFn: async () => {
      const pairs = await Promise.all(
        activeCircles.map(async (circle) => {
          try {
            const snapshot = await fetchCircleEnergySnapshotInDb(circle.id);
            return [circle.id, Math.max(0, Math.min(100, snapshot.energyScore))] as const;
          } catch {
            try {
              const completionCounts = await fetchCircleTaskCompletionCounts(circle.id);
              const activeUsers = Object.values(completionCounts).filter((n) => n > 0).length;
              const totalUsers = circle.membersCount;
              const percent = totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0;
              return [circle.id, Math.max(0, Math.min(100, percent))] as const;
            } catch {
              return [circle.id, getCompatibilityPercent(circle)] as const;
            }
          }
        }),
      );
      return Object.fromEntries(pairs) as Record<string, number>;
    },
    staleTime: 10_000,
  });

  const onInviteAction = async (inviteId: string, action: "accept" | "decline") => {
    if (!user?.id) return;
    try {
      await respondToCircleInviteInDb({ inviteId, action });
      await queryClient.invalidateQueries({ queryKey: ["panel-circle-invites", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["panel-circles-mine", user.id] });
      if (action === "accept") {
        toast({ title: "Davet kabul edildi", description: "Çembere katıldın." });
      } else {
        toast({ title: "Davet reddedildi" });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Davet işlemi başarısız";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    }
  };

  const onJoin = async (circle: CircleSummary) => {
    if (!user?.id) {
      toast({
        title: "Giriş gerekli",
        description: "Çembere katılmak için giriş yapmalısın.",
        variant: "destructive",
      });
      return;
    }

    try {
      await joinCircleInDb(circle.id);
      await queryClient.invalidateQueries({ queryKey: ["panel-circles-mine", user.id] });
      await queryClient.invalidateQueries({ queryKey: ["panel-circles-discover", user.id] });
      navigate(`/panel/cember/${circle.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Çembere katılınamadı";
      toast({ title: "Kaydedilemedi", description: msg, variant: "destructive" });
    }
  };

  return (
    <div className="mx-auto w-full max-w-7xl space-y-10">
      <header className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Çember Topluluğu</h1>
          <p className="mt-2 text-base text-slate-500 dark:text-slate-400">Sayıların rehberliğinde ruhsal yolculuğunu paylaş.</p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Çember Ara"
              className="h-10 min-w-[210px] rounded-xl border-slate-300 pl-9"
            />
          </div>

          <Button asChild className="rounded-xl">
            <Link to="/panel/cember/yeni">
              <Plus className="mr-2 h-4 w-4" /> Yeni Çember Kur
            </Link>
          </Button>
        </div>
      </header>

      {!user?.id && !loading ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/30 dark:bg-amber-900/10 dark:text-amber-200">
          Çemberleri veritabanından çekmek için giriş yapmalısın.
        </div>
      ) : null}

      {myCirclesQuery.isError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200">
          Aktif çemberler yüklenemedi: {myCirclesQuery.error instanceof Error ? myCirclesQuery.error.message : "Bilinmeyen hata"}
        </div>
      ) : null}

      {inviteInboxQuery.isError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200">
          Davet kutusu yüklenemedi: {inviteInboxQuery.error instanceof Error ? inviteInboxQuery.error.message : "Bilinmeyen hata"}
        </div>
      ) : null}

      {discoverQuery.isError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/10 dark:text-rose-200">
          Önerilen çemberler yüklenemedi: {discoverQuery.error instanceof Error ? discoverQuery.error.message : "Bilinmeyen hata"}
        </div>
      ) : null}

      {pendingInvites.length ? (
        <section className="space-y-4">
          <div className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            <Inbox className="h-5 w-5" /> Davet Kutusu
          </div>
          <div className="space-y-3">
            {pendingInvites.map((invite) => (
              <div key={invite.id} className="rounded-2xl border border-[var(--panel-primary)]/30 bg-[var(--panel-primary)]/5 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-white">{invite.circleName}</div>
                    <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                      Davet eden: {invite.senderName}
                      {invite.matchScore !== null ? ` • Uyum: %${invite.matchScore}` : ""}
                    </div>
                    {invite.message ? <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">“{invite.message}”</div> : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => void onInviteAction(invite.id, "decline")}>
                      <X className="mr-1 h-3.5 w-3.5" /> Reddet
                    </Button>
                    <Button size="sm" className="rounded-lg" onClick={() => void onInviteAction(invite.id, "accept")}>
                      <Check className="mr-1 h-3.5 w-3.5" /> Kabul Et
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold tracking-tight">Aktif Çemberlerim</h2>
          <Button variant="link" className="h-auto p-0 text-[var(--panel-primary)]" asChild>
            <Link to="/panel/cember">Tümünü Gör</Link>
          </Button>
        </div>

        {myCirclesQuery.isLoading ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-6 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
            Çemberler yükleniyor...
          </div>
        ) : filteredActiveCircles.length ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filteredActiveCircles.map((circle) => (
              <CircleCard
                key={circle.id}
                circle={circle}
                energyPercent={energyScoresQuery.data?.[circle.id] ?? getCompatibilityPercent(circle)}
                onOpen={() => navigate(`/panel/cember/${circle.id}`)}
                onEdit={circle.ownerId === user?.id ? () => navigate(`/panel/cember/yeni?edit=${circle.id}`) : null}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
            {normalizedSearchTerm
              ? "Aramaya uygun aktif çember bulunamadı."
              : "Henüz bir çembere katılmadın. Yeni bir çember kurarak başlayabilirsin."}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Senin İçin Önerilen Çemberler</h2>
          <span className="rounded bg-[var(--panel-primary)]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[var(--panel-primary)]">
            Keşfet
          </span>
        </div>

        {discoverQuery.isLoading ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-6 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
            Çemberler yükleniyor...
          </div>
        ) : filteredRecommendedCircles.length ? (
          <div className="space-y-4">
            {filteredRecommendedCircles.map((circle) => (
              <RecommendedRow
                key={circle.id}
                circle={circle}
                onOpen={() => navigate(`/panel/cember/${circle.id}`)}
                onJoin={() => void onJoin(circle)}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-100 bg-white p-6 text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
            {normalizedSearchTerm ? "Aramana uygun önerilen çember bulunamadı." : "Şu an önerilecek yeni çember yok."}
          </div>
        )}
      </section>
    </div>
  );
}
